import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Space, Alert, InputNumber, Slider } from 'antd';
import { QrcodeOutlined, CameraOutlined, ZoomInOutlined } from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';
import { usePayment, PaymentResult, PaymentError } from '../hooks/usePayment';
import { canChangeAmount } from '../../../shared/config/runtime.config';

interface PaymentFormProps {
  onSubmit?: (onayId: string, amount?: number) => void;
  onSuccess?: (result: PaymentResult) => void;
  onError?: (error: PaymentError) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onSubmit, onSuccess, onError }) => {
  const [form] = Form.useForm();
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomValue, setZoomValue] = useState(1);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1 });
  const { createPayment, isLoading, error } = usePayment();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const amountEditable = canChangeAmount();

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (scannerRef.current) {
        const scanner = scannerRef.current;
        if (scanner.isScanning) {
          scanner.stop().catch((err) => {
            console.log('Scanner cleanup error (expected if not running):', err);
          });
        }
        scannerRef.current = null;
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      setScanError('');
      setScanning(true);

      // Stop existing scanner if running
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }

      // Create new scanner instance
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode('qr-reader');
      }

      const config = {
        fps: 30, // Maximum practical FPS to match camera hardware limit
        qrbox: { width: 300, height: 80 }, // Narrow height for barcode scanning
        formatsToSupport: [
          // Only barcode formats (no QR codes)
          'CODE_128',
          'CODE_39',
          'CODE_93',
          'EAN_13',
          'EAN_8',
          'UPC_A',
          'UPC_E',
          'ITF',
          'CODABAR',
        ],
      };

      await scannerRef.current.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          // Successfully scanned - clean the scanned text
          const digitsOnly = decodedText.replace(/\D/g, '').slice(0, 19);
          form.setFieldsValue({ onayId: digitsOnly });
          stopScanning();
        },
        () => {
          // Scanning error (can be ignored for continuous scanning)
        }
      );

      // Check for zoom support
      try {
        const capabilities = await scannerRef.current.getRunningTrackCapabilities();
        const caps = capabilities as any;
        if (caps.zoom) {
          setZoomSupported(true);
          const minZoom = caps.zoom.min || 1;
          const maxZoom = caps.zoom.max || 1;
          setZoomRange({ min: minZoom, max: maxZoom });

          // Set initial zoom to 2x or middle of range for better close-up focus
          const initialZoom = Math.min(2, maxZoom);
          setZoomValue(initialZoom);

          // Apply initial zoom
          await scannerRef.current.applyVideoConstraints({
            advanced: [{ zoom: initialZoom } as any],
          });

          console.log('Zoom supported:', caps.zoom, 'Initial zoom:', initialZoom);
        }
      } catch (err) {
        console.log('Zoom not available:', err);
      }
    } catch (err) {
      setScanError('Failed to start camera. Please check permissions.');
      setScanning(false);
      scannerRef.current = null;
    }
  };

  const handleZoomChange = async (value: number) => {
    setZoomValue(value);
    if (scannerRef.current) {
      try {
        await scannerRef.current.applyVideoConstraints({
          advanced: [{ zoom: value } as any],
        });
      } catch (err) {
        console.error('Failed to apply zoom:', err);
      }
    }
  };

  const stopScanning = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async (values: { onayId: string; amount?: number }) => {
    // Use amount from form if editable, otherwise send undefined (server will use fixed amount)
    const amount = amountEditable ? values.amount! : undefined;

    // Notify parent that submit was clicked (show progress)
    if (onSubmit) {
      onSubmit(values.onayId, amount);
    }

    const result = await createPayment(values.onayId, amount);

    if (result.success) {
      // Reset form after successful payment
      form.resetFields();

      if (onSuccess) {
        onSuccess(result.data);
      }
    } else {
      // Payment failed
      if (onError) {
        onError(result.error);
      }
    }
  };

  return (
    <>
      {(error || scanError) && (
        <Alert
          message="Ошибка"
          description={error || scanError}
          type="error"
          showIcon
          closable
          onClose={() => {
            setScanError('');
          }}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ amount: 100 }}
      >
        <Form.Item
          label="Onay ID"
          name="onayId"
          rules={[
            { required: true, message: 'Пожалуйста, введите Onay ID!' },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject();
                }
                // Check if value contains only digits
                if (!/^\d+$/.test(value)) {
                  return Promise.reject(new Error('Onay ID должен содержать только цифры!'));
                }
                if (value.length !== 19) {
                  return Promise.reject(new Error('Onay ID должен содержать 19 цифр!'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            prefix={<QrcodeOutlined />}
            placeholder="Введите Onay ID (19 цифр)"
            size="large"
            onChange={(e) => {
              // Remove all non-digit characters first
              const digitsOnly = e.target.value.replace(/\D/g, '');
              // Limit to 19 digits after stripping
              const limited = digitsOnly.slice(0, 19);
              form.setFieldsValue({ onayId: limited });
            }}
            onPaste={(e) => {
              // Handle paste event explicitly for mobile browsers
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const digitsOnly = pastedText.replace(/\D/g, '').slice(0, 19);
              form.setFieldsValue({ onayId: digitsOnly });
            }}
          />
        </Form.Item>

        {amountEditable && (
          <Form.Item
            label="Сумма"
            name="amount"
            rules={[
              { required: true, message: 'Пожалуйста, введите сумму!' },
              { type: 'number', min: 1, message: 'Сумма должна быть не менее 1!' }
            ]}
          >
            <InputNumber
              placeholder="Введите сумму"
              size="large"
              style={{ width: '100%' }}
              min={1}
              step={10}
            />
          </Form.Item>
        )}

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!scanning ? (
            <Button
              type="default"
              icon={<CameraOutlined />}
              onClick={startScanning}
              block
              size="large"
            >
              Сканировать штрихкод
            </Button>
          ) : (
            <Button
              type="default"
              onClick={stopScanning}
              block
              size="large"
              danger
            >
              Остановить сканирование
            </Button>
          )}

          <div
            style={{
              width: '100%',
              maxWidth: '500px',
              maxHeight: '400px',
              display: scanning ? 'block' : 'none',
              marginTop: '16px',
              position: 'relative',
              overflow: 'hidden',
              margin: '16px auto 0',
            }}
          >
            <div id="qr-reader" style={{ width: '100%', height: '100%' }} />
            {/* Aiming indicator - horizontal line */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                height: '2px',
                background: 'rgba(0, 255, 0, 0.6)',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
            {/* Vertical center markers */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '10%',
                transform: 'translateY(-50%)',
                width: '2px',
                height: '40px',
                background: 'rgba(0, 255, 0, 0.6)',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                right: '10%',
                transform: 'translateY(-50%)',
                width: '2px',
                height: '40px',
                background: 'rgba(0, 255, 0, 0.6)',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
          </div>

          {/* Zoom control */}
          {scanning && zoomSupported && (
            <div style={{ marginTop: '16px', padding: '0 16px' }}>
              <Space style={{ width: '100%' }} direction="vertical" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ZoomInOutlined />
                  <Slider
                    min={zoomRange.min}
                    max={zoomRange.max}
                    step={0.1}
                    value={zoomValue}
                    onChange={handleZoomChange}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: '40px', textAlign: 'right' }}>
                    {zoomValue.toFixed(1)}x
                  </span>
                </div>
              </Space>
            </div>
          )}

          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            disabled={scanning}
            loading={isLoading}
          >
            Отправить 120 тенге
          </Button>
        </Space>
      </Form>
    </>
  );
};

export default PaymentForm;
